import { ClientError, gql } from "graphql-request";

import { type UserInviteNextAction, type UserInviteValidity, type ValidationError } from "~/gql/graphqlBase.generated";
import publicClient from "~/services/publicClient";

const PUBLIC_USER_INVITE_BY_CODE = gql`
	query PublicUserInviteByCode($code: String!) {
		publicUserInviteByCode(code: $code) {
			validity
			nextAction
			recipientEmail
			changePasswordId
		}
	}
`;

const PUBLIC_USER_INVITE_ACCEPT = gql`
	mutation PublicUserInviteAccept($input: AcceptUserInviteInput!) {
		publicUserInviteAccept(input: $input) {
			validity
			accepted
			errors {
				code
				message
			}
		}
	}
`;

export const SET_PASSWORD_FROM_EMAIL = "SET_PASSWORD_FROM_EMAIL";

export type PublicInviteNextAction = UserInviteNextAction | typeof SET_PASSWORD_FROM_EMAIL;

export interface PublicInvite {
	validity: UserInviteValidity;
	nextAction: PublicInviteNextAction;
	recipientEmail: Maybe<string>;
	changePasswordId: Maybe<string>;
}

export const INVITE_REQUIRES_SIGN_IN_CODE = "INVITE_REQUIRES_SIGN_IN";

const PUBLIC_INVITE_REQUEST_FAILED = "Public invite request failed";

export interface AcceptInviteInput {
	code: string;
	firstName: string;
	lastName: string;
	phone: string;
}

export interface AcceptInviteResult {
	validity: UserInviteValidity;
	accepted: boolean;
	errors: Maybe<ValidationError[]>;
}

export class PublicInviteRequestError extends Error {
	readonly codes: string[];
	readonly status: number;

	constructor(status: number, codes: string[]) {
		super(PUBLIC_INVITE_REQUEST_FAILED);
		this.name = "PublicInviteRequestError";
		this.codes = codes;
		this.status = status;
	}
}

export const publicInviteRequestContext = (cause: unknown): Record<string, unknown> | undefined =>
	cause instanceof PublicInviteRequestError ? { codes: cause.codes, status: cause.status } : undefined;

// graphql-request's ClientError message embeds the request variables (the invite code) and any partial response (a credential).
const sanitizeRequestError = (cause: unknown): unknown => {
	if (!(cause instanceof ClientError)) return cause;
	const codes = (cause.response.errors ?? [])
		.map((error) => error.extensions?.code)
		.filter((code): code is string => typeof code === "string");
	return new PublicInviteRequestError(cause.response.status, codes);
};

export async function fetchInviteByCode(code: string): Promise<PublicInvite> {
	try {
		const inviteResponse = await publicClient.request<{ publicUserInviteByCode: PublicInvite }>(
			PUBLIC_USER_INVITE_BY_CODE,
			{ code }
		);
		return inviteResponse.publicUserInviteByCode;
	} catch (cause) {
		throw sanitizeRequestError(cause);
	}
}

export async function acceptInvite(input: AcceptInviteInput): Promise<AcceptInviteResult> {
	try {
		const acceptResponse = await publicClient.request<{ publicUserInviteAccept: AcceptInviteResult }>(
			PUBLIC_USER_INVITE_ACCEPT,
			{ input }
		);
		return acceptResponse.publicUserInviteAccept;
	} catch (cause) {
		throw sanitizeRequestError(cause);
	}
}
